import { createFocusTrap } from 'focus-trap';
import { getFocusableElements, debugError } from '@utils';
import { pubsub } from '@services';

/**
 * Drawer Usage:
 * Call the open(), close() or toggle() method to toggle the drawer
 *
 *
 * Drawer Toggle controllers:
 * Add these data attributes to any element to make it control a drawer:
 *
 * aria-controls="drawer-id"    // Required: unique ID of the drawer to control
 * aria-expanded="false"        // Required: 'true' or 'false'
 * data-drawer-action="toggle"  // Optional: 'open', 'close', or 'toggle' (default)
 *
 * Example:
 * <button aria-controls="cart-drawer" aria-expanded="false">Open Cart</button>
 */

export class DrawerElement extends HTMLElement {
  static {
    // Let the document listen for clicks on drawer controllers
    document.addEventListener('click', (event) => {
      const controller = event.target.closest('[data-drawer-controller]');
      const drawerId = controller?.getAttribute('aria-controls');
      if (!drawerId) return;

      if (drawerId === 'cart-drawer' && window.theme?.cart_icon_behavior === 'page') {
        window.location.href = window.routes.cart_url;
        event.preventDefault();
        return;
      }

      const { drawerAction } = controller.dataset;
      const drawer = document.getElementById(drawerId);
      if (!drawer) {
        debugError(`Drawer with id '${drawerId}' not found`);
        return;
      }
      drawer.handleAction(drawerAction);
      event.preventDefault();
    });
  }

  connectedCallback() {
    // Set the options
    const { subscribeEventName, subscribeEventAction } = this.dataset;
    this.options = { subscribeEventName, subscribeEventAction };

    // Get the modal body
    this.modalBody = this.querySelector('[data-drawer-body]');
    if (!this.modalBody) return;

    // Listen for key:escape events
    this.unsubscribeEscapeKey = pubsub.subscribe('key:escape', () => this.close());

    // Create the focus trap
    this.trap = createFocusTrap(this.modalBody, {
      // Allow clicks outside the modal to close the drawer
      allowOutsideClick: true,
      initialFocus: false,
    });

    // Activate the trap if the drawer is rendered open
    if (this.ariaHidden === 'false') {
      this.trap.activate();
    }

    // Subscribe to the event
    this.subscribeEvents();

    this.#handleShopifyEvents();

    // Close drawer when clicking on navigation links
    this.#handleLinkClicks();
  }

  disconnectedCallback() {
    this.unsubscribeEscapeKey();
  }

  /**
   * Handle drawer actions
   * @param {string} action - The action to perform ('open', 'close', or 'toggle')
   */
  handleAction(action) {
    switch (action) {
      case 'open':
        this.open();
        break;
      case 'close':
        this.close();
        break;
      default:
        this.toggle();
        break;
    }
  }

  /**
   * Handle drawer controls
   */
  updateControllers() {
    document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((element) => {
      element.setAttribute('aria-expanded', this.ariaHidden === 'false' ? 'true' : 'false');
    });
  }

  /**
   * Open the drawer
   */
  /**
   * Open the drawer.
   * @param {{ editorPreview?: boolean }} [options] - `editorPreview: true` opens the drawer
   *   for display only: no focus trap and no body-scroll lock. Used when the theme editor
   *   selects the drawer's section, where the merchant must keep using the editor around it.
   */
  open({ editorPreview = false } = {}) {
    if (!editorPreview) {
      // Activate the focus trap after the transition ends
      const handleTransition = (event) => {
        if (event.target !== this.modalBody) return;
        this.trap.activate();
        this.modalBody.removeEventListener('transitionend', handleTransition);
      };
      // Only listen for transitionend when opening the drawer
      this.modalBody.addEventListener('transitionend', handleTransition);
    }

    this.ariaHidden = false;
    this.toggleBackdrop(false);
    this.updateControllers();

    // Remember whether THIS drawer locked the body, so close() can only unlock what it
    // locked. Without the flag an editor-preview drawer (which never locks) still ran
    // unlockBodyScroll() on close, decrementing the shared `openDrawers` counter and
    // clearing body overflow while another drawer was still open. Qodo review, PR #126.
    this.lockedBodyScroll = !editorPreview;
    if (this.lockedBodyScroll) {
      // Lock body scroll to prevent background scrolling
      this.lockBodyScroll();
    }

    this.publishEvent('drawer:opened');
  }

  /**
   * Close the drawer
   */
  close() {
    this.trap.deactivate();
    setTimeout(() => {
      this.ariaHidden = true;
      this.toggleBackdrop(true);
      this.updateControllers();

      // Unlock body scroll — only if this drawer was the one that locked it.
      if (this.lockedBodyScroll) {
        this.lockedBodyScroll = false;
        this.unlockBodyScroll();
      }

      this.publishEvent('drawer:closed');
    }, 100);
  }

  /**
   * Lock body scroll to prevent background scrolling when drawer is open
   */
  lockBodyScroll() {
    // Track the number of open drawers to handle nested/multiple drawers
    document.body.dataset.openDrawers = (
      parseInt(document.body.dataset.openDrawers || '0') + 1
    ).toString();
    document.body.style.overflow = 'hidden';
  }

  /**
   * Unlock body scroll when drawer is closed
   */
  unlockBodyScroll() {
    const openDrawers = parseInt(document.body.dataset.openDrawers || '1') - 1;
    document.body.dataset.openDrawers = Math.max(0, openDrawers).toString();

    // Only unlock if no other drawers are open
    if (openDrawers <= 0) {
      document.body.style.overflow = '';
      delete document.body.dataset.openDrawers;
    }
  }

  /**
   * Toggle the drawer
   */
  toggle() {
    this.ariaHidden === 'true' ? this.open() : this.close();
  }

  /**
   * Toggle the backdrop
   * @param {boolean} hide - Whether to hide the backdrop
   */
  toggleBackdrop(hide) {
    this.backdrop = this.backdrop || this.querySelector('[data-backdrop]');
    this.backdrop.ariaHidden = hide;
  }

  /**
   * Update the trap container content elements, always add the header and footer.
   * @param {HTMLElement} element - The new element to trap focus on
   */
  updateTrapContainerElements(element) {
    this.drawerHeader = this.drawerHeader || this.querySelector('.drawer__header');
    this.drawerFooter = this.drawerFooter || this.querySelector('.drawer__footer');
    const elementsArray = [this.drawerHeader, element, this.drawerFooter].filter(Boolean); // Remove null/undefined elements

    this.trap.updateContainerElements(elementsArray);

    // Get focusable elements from each container and combine them
    const focusableElements = elementsArray.flatMap((el) => getFocusableElements(el));

    // Focus the first focusable element if it exists
    focusableElements?.[0]?.focus();
  }

  /**
   * Publish the event
   */
  publishEvent(eventName) {
    pubsub.publish(`${eventName}:${this.id}`, {
      target: this.id,
    });
  }

  /**
   * Subscribe to the event
   */
  subscribeEvents() {
    if (!this.options.subscribeEventName || !this.options.subscribeEventAction) return;

    pubsub.subscribe(this.options.subscribeEventName, () => {
      this.handleAction(this.options.subscribeEventAction);
    });
  }

  /**
   * Handle block select events
   */
  #handleShopifyEvents() {
    // Auto-open ONLY on an explicit select of the section that owns THIS drawer, so a
    // merchant selecting e.g. the menu drawer can see and style it.
    //
    // `shopify:section:load` is deliberately NOT in this list any more. The theme editor
    // re-renders a section on every setting change and on its initial load, so reacting to
    // it auto-opened the drawer at moments nobody asked for — and an open drawer puts its
    // full-viewport backdrop over the preview, traps focus and locks scrolling. The symptom
    // was: one click in the preview works, then no section can be selected until a full
    // reload, with nothing visibly open. NGE-9614.
    //
    // Two further guards: `target.contains(this)` so only the drawer belonging to the
    // selected section reacts (the old code opened whichever drawer it found first in that
    // section), and `editorPreview` so the drawer does not trap focus or lock body scroll
    // while the merchant is working around it.
    document.addEventListener('shopify:section:select', (event) => {
      if (!event.target.contains(this)) return;
      this.open({ editorPreview: true });
    });

    document.addEventListener('shopify:section:deselect', (event) => {
      if (!event.target.contains(this)) return;
      this.close();
    });
  }

  /**
   * Close drawer when clicking on navigation links inside the drawer
   * This handles anchor links and external URLs that would otherwise leave the drawer open
   */
  #handleLinkClicks() {
    this.modalBody.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link) return;

      // Skip if the link is a drawer controller (those handle their own logic)
      if (link.hasAttribute('data-drawer-controller')) return;

      // Skip if the link has no href or is just "#"
      const href = link.getAttribute('href');
      if (!href || href === '#') return;

      // Close the drawer when navigating
      this.close();
    });
  }
}

customElements.define('drawer-element', DrawerElement);
